/**
 * Sustainable Water (Gold) — the client-side half, issue #364.
 *
 * The frontend holds no water data. Per-litre tariffs, state factors, on-site
 * energy per category and the purchased domestic and international rates are
 * resolved server-side and never reach the client; this file is display copy
 * plus the two rules the schema cannot express.
 *
 * An add-row assessment: one row per water category and use, the same shape as
 * Procurement, Eco Office and Heavy Equipment. Five columns, so unlike Heavy
 * Equipment it keeps the shared table at xl and only stacks below it.
 */

/**
 * `CalcCategories.SUSTAINABLE_WATER` is `"Sustainable Water"` and that is what
 * both `/calculator/categories` and `/schema` send, but `catalog_metadata()`
 * returns `"Water"`, so the certification list says something different. Match
 * either, the same way the Heavy Equipment catalogue does.
 */
export const WATER_CATEGORIES = ["Sustainable Water", "Water"] as const;

export const WATER_CATEGORY = WATER_CATEGORIES[0];

export const isSustainableWaterCategory = (category: string | null): boolean =>
  category !== null &&
  (WATER_CATEGORIES as readonly string[]).includes(category);

/**
 * Field key by served column id, straight from the manifest's
 * `legacy_column_id`.
 *
 * The rules below are keyed by field key rather than label, matching Heavy
 * Equipment, so a reseed that renumbered anything fails the test rather than
 * silently pointing a rule at the wrong column.
 */
export const WATER_FIELD_KEY_BY_COLUMN_ID: Record<number, string> = {
  1: "water_category",
  2: "primary_use",
  3: "primary_use_other",
  4: "water_source",
  5: "weekly_litres",
};

export const waterFieldKeyOf = (columnId: number | string): string =>
  WATER_FIELD_KEY_BY_COLUMN_ID[Number(columnId)] ?? "";

const COLUMN_ID_BY_FIELD_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(WATER_FIELD_KEY_BY_COLUMN_ID).map(([id, key]) => [key, id]),
);

export const waterColumnIdOf = (fieldKey: string): string =>
  COLUMN_ID_BY_FIELD_KEY[fieldKey] ?? "";

/** The served labels referred to by name, for the row heading and geometry. */
export const WATER_LABELS = {
  waterCategory: "Water Category",
  primaryUse: "Primary Use",
  primaryUseOther: "Other Primary Use",
  waterSource: "Water Source",
  weeklyLitres: "Weekly Quantity (L/week)",
} as const;

/** The one `primary_use` answer that asks for a written description. */
export const PRIMARY_USE_OTHER = "Other";

/** A row's answers by field key. */
export type WaterRowValues = Record<string, string>;

/**
 * Rekey a row's answers from column id to field key, which is what the rules
 * below are written in. Form state is keyed by column id throughout.
 */
export const waterRowValuesByFieldKey = (
  values: Record<string, string>,
): WaterRowValues => {
  const rekeyed: WaterRowValues = {};
  for (const [columnId, value] of Object.entries(values)) {
    const fieldKey = waterFieldKeyOf(columnId);
    if (fieldKey) rekeyed[fieldKey] = value;
  }
  return rekeyed;
};

const hasValue = (value: string | undefined): boolean =>
  value !== undefined && value.trim() !== "";

/**
 * The field keys this row should be asking about.
 *
 * Only one rule: `primary_use_other` applies when Primary Use is exactly
 * "Other". The schema serves that column as plain text with no conditional
 * marker, so there is nothing to derive it from and it lives here.
 *
 * Water Source narrowing is deliberately absent. The schema expresses that one
 * through `conditional_allowed_values`, which `Calculator` already handles for
 * any assessment, so restating it here would be a second copy to keep in step.
 */
export const waterVisibleFieldKeysFor = (
  values: WaterRowValues,
): Set<string> => {
  const visible = new Set(Object.values(WATER_FIELD_KEY_BY_COLUMN_ID));
  if (values.primary_use !== PRIMARY_USE_OTHER) {
    visible.delete("primary_use_other");
  }
  return visible;
};

/**
 * Field keys this answer just stopped applying to, so their values can be
 * cleared alongside it.
 *
 * Unlike Transport, Eco Office and Heavy Equipment, `value_service` has no
 * branch for Sustainable Water, so nothing is soft-deleted server-side. Leaving
 * it means a description written under "Other" survives a change of use and is
 * echoed back in the result payload. Clearing it here is the only cleanup there
 * is.
 */
export const waterDroppedFieldKeysFor = (
  before: WaterRowValues,
  after: WaterRowValues,
): string[] => {
  const visibleAfter = waterVisibleFieldKeysFor(after);
  return [...waterVisibleFieldKeysFor(before)].filter(
    (fieldKey) => !visibleAfter.has(fieldKey),
  );
};

/**
 * Visible questions this row has not answered yet.
 *
 * Mirrors `WaterRowInput.is_complete` server-side rather than the schema's
 * `is_required` flags, because the two disagree about a zero. `compute` raises
 * one identical string for every kind of incompleteness, naming neither the
 * row nor the field, so the specific answer has to be worked out here.
 *
 * A zero row is legal and `is_complete` asks nothing further of it, not even
 * a primary use, so neither does this.
 */
export const waterMissingFieldKeysFor = (values: WaterRowValues): string[] => {
  const started = Object.values(values).some(hasValue);
  if (!started) return [];

  const litres = values.weekly_litres;
  if (!isNonNegativeLitres(litres)) return ["weekly_litres"];

  const describesOther =
    values.primary_use === PRIMARY_USE_OTHER &&
    !hasValue(values.primary_use_other);

  // A zero row is finished once any written description it promised is there.
  if (Number(litres) === 0) return describesOther ? ["primary_use_other"] : [];

  return [...waterVisibleFieldKeysFor(values)].filter((fieldKey) => {
    if (fieldKey === "primary_use_other") return describesOther;
    if (fieldKey === "weekly_litres") return false;
    return !hasValue(values[fieldKey]);
  });
};

/**
 * Weekly litres must be a number and must not be negative.
 *
 * Zero is legal and meaningful: the row model is `Field(ge=0)` and
 * `is_complete` returns true for a zero row. Only a negative, a blank or
 * something that is not a number is wrong.
 */
export const isNonNegativeLitres = (value: string | undefined): boolean => {
  if (!hasValue(value)) return false;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0;
};

/**
 * Help text for the questions whose wording does not carry itself. The schema
 * describes only Weekly Quantity, and that one is served, so it is absent here.
 */
export const WATER_TOOLTIPS_BY_FIELD_KEY: Record<string, string> = {
  water_category:
    "The grade of water this row is about. Add a row for each grade your lab uses.",
  primary_use:
    "What the water is mostly used for. Pick Other if nothing in the list fits and describe it in the next column.",
  primary_use_other:
    "Only asked when Primary Use is Other. A few words is plenty.",
  water_source:
    "Where the water comes from. The options narrow to those that make sense for the grade you picked.",
  weekly_litres:
    "Litres a week across the whole lab, not per person. Estimates are fine, but it has to be more than zero.",
};

/** Compact header text for the wide table; the stacked cards keep full labels. */
export const WATER_HEADER_LABELS: Record<string, string> = {
  [WATER_LABELS.waterCategory]: "Water",
  [WATER_LABELS.primaryUse]: "Primary use",
  [WATER_LABELS.primaryUseOther]: "If other",
  [WATER_LABELS.waterSource]: "Source",
  [WATER_LABELS.weeklyLitres]: "L / week",
};

/**
 * Twelfths. The five columns plus Remove must total 12, or `table-fixed` scales
 * everything down until labels break mid-word.
 *
 * Water Category and Primary Use carry the long option text ("Experiment /
 * General Experimental Use") and take the width; litres needs a few digits.
 */
export const WATER_COLUMN_WIDTH: Record<string, string> = {
  [WATER_LABELS.waterCategory]: "w-3/12",
  [WATER_LABELS.primaryUse]: "w-3/12",
  [WATER_LABELS.primaryUseOther]: "w-2/12",
  [WATER_LABELS.waterSource]: "w-2/12",
  [WATER_LABELS.weeklyLitres]: "w-1/12",
};

